import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, Ban, CalendarX } from "lucide-react";
import { formatDateBR } from "@/lib/format";
import { Header, Field, inputCls, saveCls } from "@/components/admin/ServicesTab";

export default function BlocksTab({ barbers }) {
  const [exceptions, setExceptions] = useState([]);
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);

  const [excForm, setExcForm] = useState({ date: "", barber_id: "", reason: "" });
  const [blkForm, setBlkForm] = useState({ date: "", barber_id: "", start: "09:00", end: "10:00", reason: "" });
  const [savingE, setSavingE] = useState(false);
  const [savingB, setSavingB] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [e, b] = await Promise.all([api.get("/admin/exceptions"), api.get("/admin/blocks")]);
      setExceptions(e.data); setBlocks(b.data);
    } catch { toast.error("Erro ao carregar."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const barberName = (id) => id ? (barbers.find((b) => b.id === id)?.name || "—") : "Todos os profissionais";

  const addException = async () => {
    if (!excForm.date) return toast.error("Selecione a data.");
    setSavingE(true);
    try {
      await api.post("/admin/exceptions", { date: excForm.date, barber_id: excForm.barber_id || null, reason: excForm.reason || null });
      toast.success("Dia sem atendimento cadastrado.");
      setExcForm({ date: "", barber_id: "", reason: "" }); load();
    } catch { toast.error("Erro ao salvar."); }
    finally { setSavingE(false); }
  };

  const addBlock = async () => {
    if (!blkForm.date) return toast.error("Selecione a data.");
    if (blkForm.start >= blkForm.end) return toast.error("Horário final deve ser após o inicial.");
    setSavingB(true);
    try {
      await api.post("/admin/blocks", {
        date: blkForm.date, barber_id: blkForm.barber_id || null,
        start: blkForm.start, end: blkForm.end, reason: blkForm.reason || null,
      });
      toast.success("Bloqueio cadastrado.");
      setBlkForm({ date: "", barber_id: "", start: "09:00", end: "10:00", reason: "" }); load();
    } catch { toast.error("Erro ao salvar."); }
    finally { setSavingB(false); }
  };

  const delException = async (id) => { await api.delete(`/admin/exceptions/${id}`); load(); };
  const delBlock = async (id) => { await api.delete(`/admin/blocks/${id}`); load(); };

  return (
    <div className="fade-in">
      <Header title="Bloqueios & Folgas" />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Full day off */}
        <div className="card-surface p-5">
          <h3 className="font-sans2 font-semibold text-amber-100 flex items-center gap-2 mb-4">
            <CalendarX className="w-5 h-5 text-[#D4AF37]" /> Dia sem atendimento / Feriado
          </h3>
          <div className="space-y-3">
            <Field label="Data"><input type="date" data-testid="exc-date-input" value={excForm.date} onChange={(e) => setExcForm({ ...excForm, date: e.target.value })} className={inputCls} /></Field>
            <Field label="Profissional">
              <select data-testid="exc-barber-select" value={excForm.barber_id} onChange={(e) => setExcForm({ ...excForm, barber_id: e.target.value })} className={inputCls}>
                <option value="">Todos os profissionais</option>
                {barbers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Motivo (opcional)"><input data-testid="exc-reason-input" value={excForm.reason} onChange={(e) => setExcForm({ ...excForm, reason: e.target.value })} className={inputCls} placeholder="Ex.: Feriado, folga" /></Field>
            <button data-testid="add-exception-btn" onClick={addException} disabled={savingE} className={saveCls}>
              {savingE && <Loader2 className="w-4 h-4 animate-spin" />} Adicionar dia
            </button>
          </div>
          <div className="mt-5 space-y-2">
            {exceptions.map((e) => (
              <div key={e.id} data-testid={`exception-item-${e.id}`} className="flex items-center justify-between bg-[#0A0B0E]/50 rounded-lg px-3 py-2 text-sm">
                <div>
                  <span className="text-amber-50 font-mono2">{formatDateBR(e.date)}</span>
                  <span className="text-stone-400 font-sans2 ml-2">{barberName(e.barber_id)}</span>
                  {e.reason && <span className="text-stone-500 text-xs italic ml-2">— {e.reason}</span>}
                </div>
                <button onClick={() => delException(e.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {exceptions.length === 0 && !loading && <p className="text-stone-500 text-sm font-sans2">Nenhum dia cadastrado.</p>}
          </div>
        </div>

        {/* Time block */}
        <div className="card-surface p-5">
          <h3 className="font-sans2 font-semibold text-amber-100 flex items-center gap-2 mb-4">
            <Ban className="w-5 h-5 text-[#C57B57]" /> Bloqueio de período
          </h3>
          <div className="space-y-3">
            <Field label="Data"><input type="date" data-testid="blk-date-input" value={blkForm.date} onChange={(e) => setBlkForm({ ...blkForm, date: e.target.value })} className={inputCls} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Início"><input type="time" data-testid="blk-start-input" value={blkForm.start} onChange={(e) => setBlkForm({ ...blkForm, start: e.target.value })} className={inputCls} /></Field>
              <Field label="Fim"><input type="time" data-testid="blk-end-input" value={blkForm.end} onChange={(e) => setBlkForm({ ...blkForm, end: e.target.value })} className={inputCls} /></Field>
            </div>
            <Field label="Profissional">
              <select data-testid="blk-barber-select" value={blkForm.barber_id} onChange={(e) => setBlkForm({ ...blkForm, barber_id: e.target.value })} className={inputCls}>
                <option value="">Todos os profissionais</option>
                {barbers.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </Field>
            <Field label="Motivo (opcional)"><input data-testid="blk-reason-input" value={blkForm.reason} onChange={(e) => setBlkForm({ ...blkForm, reason: e.target.value })} className={inputCls} placeholder="Ex.: manutenção, compromisso" /></Field>
            <button data-testid="add-block-btn" onClick={addBlock} disabled={savingB} className={saveCls}>
              {savingB && <Loader2 className="w-4 h-4 animate-spin" />} Adicionar bloqueio
            </button>
          </div>
          <div className="mt-5 space-y-2">
            {blocks.map((b) => (
              <div key={b.id} data-testid={`block-item-${b.id}`} className="flex items-center justify-between bg-[#0A0B0E]/50 rounded-lg px-3 py-2 text-sm">
                <div>
                  <span className="text-amber-50 font-mono2">{formatDateBR(b.date)} {b.start}–{b.end}</span>
                  <span className="text-stone-400 font-sans2 ml-2">{barberName(b.barber_id)}</span>
                  {b.reason && <span className="text-stone-500 text-xs italic ml-2">— {b.reason}</span>}
                </div>
                <button onClick={() => delBlock(b.id)} className="text-red-400 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
            {blocks.length === 0 && !loading && <p className="text-stone-500 text-sm font-sans2">Nenhum bloqueio cadastrado.</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
